import { ChatMessage, ordersIdMessages, ordersIdMessagesNew } from '../../../entities/chat';
import { useEffect, useState, useRef, useCallback } from 'react';

export function useChatPolling(orderId: string, intervalMs:any = 3000) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const lastMessageIdRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ✅ защита от параллельных запросов
  const isFetchingRef = useRef(false);

  const loadInitialMessages = useCallback(async () => {
    if (!orderId) return;

    try {
      setIsLoading(true);

      const response = await ordersIdMessages(orderId);

      setMessages(response);
      lastMessageIdRef.current = response.length
        ? response[response.length - 1].id
        : null;
    } catch (error) {
      console.error('Ошибка загрузки сообщений:', error);
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  const checkNewMessages = useCallback(async () => {
    if (!orderId) return;
    if (!lastMessageIdRef.current) return;

    // ✅ не запускаем новый запрос, если прошлый ещё идёт
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const params = {
        lastMessageId: lastMessageIdRef.current,
        limit: 50, // лучше number
      };

      const newMessages = await ordersIdMessagesNew(orderId, params);

      if (newMessages.length > 0) {
        lastMessageIdRef.current = newMessages[newMessages.length - 1].id;

        setMessages((prev) => {
          // ✅ защита от дублей
          const ids = new Set(prev.map((m) => m.id));
          const unique = newMessages.filter((m) => !ids.has(m.id));
          return unique.length ? [...prev, ...unique] : prev;
        });
      }
    } catch (error) {
      console.error('Ошибка polling:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [orderId]);

  useEffect(() => {
    // ✅ очищаем старый интервал перед созданием нового
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    loadInitialMessages();

    intervalRef.current = setInterval(() => {
      checkNewMessages();
    }, intervalMs);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [loadInitialMessages, checkNewMessages, intervalMs]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
    lastMessageIdRef.current = message.id;
  }, []);

  return { messages, isLoading, addMessage };
}
